import type { CSSProperties } from "react";
import { AbsoluteFill, Sequence, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { overlayWindow } from "../lib/overlayWindow.ts";
import { LEGACY_RENDER_BRAND, type RenderBrand, type RenderTextOverlay } from "./types.ts";

const FADE_FRAMES = 12;

function fontFor(role: RenderTextOverlay["fontRole"], brand: RenderBrand): string {
  return role === "punch" ? brand.punchFont : role === "speaker" ? brand.speakerFont : brand.captionFont;
}

const OneOverlay = ({ spec, brand }: { spec: RenderTextOverlay; brand: RenderBrand }) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const opacity = Math.min(
    interpolate(frame, [0, FADE_FRAMES], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
    interpolate(frame, [durationInFrames - FADE_FRAMES, durationInFrames], [1, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
  const pos: CSSProperties =
    spec.align === "center"
      ? { left: `${spec.xPct}%`, transform: "translateX(-50%)", textAlign: "center" }
      : spec.align === "end"
        ? { right: `${100 - spec.xPct}%`, textAlign: "right" }
        : { left: `${spec.xPct}%`, textAlign: "left" };
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div
        data-studio-el="overlay"
        style={{
          position: "absolute",
          top: `${spec.yPct}%`,
          ...pos,
          opacity,
          fontFamily: `${fontFor(spec.fontRole, brand)}, sans-serif`,
          fontWeight: 700,
          fontSize: spec.fontSizePx,
          lineHeight: 1.2,
          color: spec.color,
          whiteSpace: "pre-wrap",
          textShadow: "0 4px 12px rgba(0,0,0,0.6)",
        }}
      >
        {spec.text}
      </div>
    </AbsoluteFill>
  );
};

/** Timed text overlays (a brand's on-screen CTA). Absent/empty → renders
 *  nothing, so a channel without overlays is byte-identical to before.
 *  `durationSec` is the CONTENT duration: mounted inside the same tree as
 *  the captions, a cover-hold prefix shifts every overlay automatically. */
export const TextOverlays = ({
  overlays,
  brand,
  durationSec,
}: {
  overlays?: RenderTextOverlay[];
  brand?: RenderBrand;
  durationSec: number;
}) => {
  const { fps } = useVideoConfig();
  if (!overlays?.length) return null;
  const b = brand ?? LEGACY_RENDER_BRAND;
  return (
    <>
      {overlays.map((spec, i) => {
        const w = overlayWindow(spec, durationSec, fps);
        return (
          <Sequence key={`ov-${i}`} from={w.from} durationInFrames={w.durationInFrames}>
            <OneOverlay spec={spec} brand={b} />
          </Sequence>
        );
      })}
    </>
  );
};
